'use client';

import { cva, type VariantProps } from 'class-variance-authority';
import React, {
  createContext,
  forwardRef,
  useContext,
  useState,
} from 'react';
import { cn } from '../../lib/utils';

// Context for managing collapsible state
type CollapsibleContextType = {
  isOpen: boolean;
  toggle: () => void;
};

const CollapsibleContext = createContext<
  CollapsibleContextType | undefined
>(undefined);

function useCollapsibleContext() {
  const context = useContext(CollapsibleContext);
  if (!context) {
    throw new Error(
      'Collapsible components must be used within a CollapsibleProvider'
    );
  }
  return context;
}

// Collapsible container
const collapsibleVariants = cva(['w-full']);

export interface CollapsibleProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof collapsibleVariants> {
  defaultOpen?: boolean;
  className?: string;
  children: React.ReactNode;
}

export const Collapsible = forwardRef<
  HTMLDivElement,
  CollapsibleProps
>(function Collapsible(
  {
    defaultOpen = false,
    className,
    children,
    ...props
  }: CollapsibleProps,
  ref
) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const toggle = () => setIsOpen((prev) => !prev);

  return (
    <CollapsibleContext.Provider value={{ isOpen, toggle }}>
      <div
        ref={ref}
        className={cn(collapsibleVariants(), className)}
        {...props}
      >
        {children}
      </div>
    </CollapsibleContext.Provider>
  );
});

// Collapsible trigger button
const collapsibleTriggerVariants = cva([
  'flex w-full items-center justify-between py-2 font-medium transition-all hover:underline [&[data-state=open]>svg]:rotate-180',
]);

export interface CollapsibleTriggerProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  className?: string;
  children: React.ReactNode;
}

export const CollapsibleTrigger = forwardRef<
  HTMLButtonElement,
  CollapsibleTriggerProps
>(function CollapsibleTrigger(
  { className, children, ...props }: CollapsibleTriggerProps,
  ref
) {
  const { isOpen, toggle } = useCollapsibleContext();

  return (
    <button
      ref={ref}
      type="button"
      onClick={toggle}
      aria-expanded={isOpen}
      data-state={isOpen ? 'open' : 'closed'}
      className={cn(collapsibleTriggerVariants(), className)}
      {...props}
    >
      {children}
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="h-4 w-4 shrink-0 transition-transform duration-200"
      >
        <polyline points="6 9 12 15 18 9"></polyline>
      </svg>
    </button>
  );
});

// Collapsible content
const collapsibleContentVariants = cva([
  'overflow-hidden transition-all data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down',
]);

export interface CollapsibleContentProps
  extends React.HTMLAttributes<HTMLDivElement> {
  className?: string;
  children: React.ReactNode;
}

export const CollapsibleContent = forwardRef<
  HTMLDivElement,
  CollapsibleContentProps
>(function CollapsibleContent(
  { className, children, ...props }: CollapsibleContentProps,
  ref
) {
  const { isOpen } = useCollapsibleContext();

  if (!isOpen) {
    return null;
  }

  return (
    <div
      ref={ref}
      data-state={isOpen ? 'open' : 'closed'}
      className={cn(collapsibleContentVariants(), className)}
      {...props}
    >
      <div className="pt-2 pb-4">{children}</div>
    </div>
  );
});

export {
  collapsibleVariants,
  collapsibleTriggerVariants,
  collapsibleContentVariants,
};
